import { types, Instance, getRoot } from "mobx-state-tree";
import {
  modTypes,
  transformChangeTypes,
  findLayersOrLayerAssets,
} from "@blings/blings-player";
import type { Animation } from "@blings/blings-player";
import { motionAPI } from "@blings/blings-player/editor-tools";
import type { MotionTargetQuery } from "@blings/blings-player/editor-tools";
import type { RootInstance } from "./main";
import type {
  ElementBounds,
  Point,
  HandlePosition,
  GestureType,
  TransformState,
} from "../components/MainVidPlayer/TransformHandles/types";
import type { LayerInspectorSelection } from "../helpers/layerInspectorTools";
import {
  setLayerInspectorSelections,
  setLayerInspectorEnabled,
} from "../helpers/layerInspectorTools";
import { resolveLayerDetails } from "../helpers/layerInspectorPrefill";
import { buildLayerInspectorSelectionKey } from "../helpers/layerSelectionUtils";
import { mainAssetKey } from "./PlayerStore";

type TransformModInfo = {
  value: number;
  isAbsolute: boolean;
};

type TransformMeta = {
  scaleIsAbsolute: boolean;
  scaleXIsAbsolute: boolean;
  scaleYIsAbsolute: boolean;
  hasScaleMod: boolean;
  hasScaleXMod: boolean;
  hasScaleYMod: boolean;
};

type SelectionTransformState = {
  transform: TransformState;
  meta: TransformMeta;
};

type ResolvedLayerSnapshot = {
  assetId: string;
  layerName: string;
  layerUid?: number;
};

const isSplitVector = (value: any): boolean =>
  Boolean(value && typeof value === "object" && "s" in value);

const toPositionVector = (value: unknown): number[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const x = Number(value[0]);
  const y = Number(value[1]);
  const z = Number(value[2] ?? 0);

  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return undefined;
  }

  return [x, y, Number.isFinite(z) ? z : 0];
};

const getLayerTransformValues = (layer: any): TransformState => {
  const transformScale = layer?.ks?.s;
  const transformPosition = layer?.ks?.p;
  const transformRotation =
    layer?.ks && "r" in layer.ks ? layer.ks.r : undefined;

  const positionX = (() => {
    if (!transformPosition) return undefined;
    if (isSplitVector(transformPosition)) {
      if (transformPosition.x?.a === 0) {
        return transformPosition.x.k;
      }
      return transformPosition?.x?.k?.[0]?.s?.[0];
    }
    if (transformPosition.a === 1) {
      return transformPosition.k?.[0]?.s?.[0];
    }
    return transformPosition.k?.[0];
  })();

  const positionY = (() => {
    if (!transformPosition) return undefined;
    if (isSplitVector(transformPosition)) {
      if (transformPosition.y?.a === 0) {
        return transformPosition.y.k;
      }
      return transformPosition?.y?.k?.[0]?.s?.[0];
    }
    if (transformPosition.a === 1) {
      return transformPosition.k?.[0]?.s?.[1];
    }
    return transformPosition.k?.[1];
  })();

  const scaleX = (() => {
    if (!transformScale) return undefined;
    if (transformScale.a === 0) {
      return transformScale.k?.[0];
    }
    return transformScale.k?.[0]?.s?.[0];
  })();

  const scaleY = (() => {
    if (!transformScale) return undefined;
    if (transformScale.a === 0) {
      return transformScale.k?.[1];
    }
    return transformScale.k?.[0]?.s?.[1];
  })();

  const rotation = (() => {
    if (!transformRotation) return undefined;
    if (transformRotation.a === 0) {
      return transformRotation.k;
    }
    return transformRotation.k?.[0]?.s?.[0];
  })();

  return {
    positionX: typeof positionX === "number" ? positionX : undefined,
    positionY: typeof positionY === "number" ? positionY : undefined,
    scaleX: typeof scaleX === "number" ? scaleX : undefined,
    scaleY: typeof scaleY === "number" ? scaleY : undefined,
    rotation: typeof rotation === "number" ? rotation : undefined,
  };
};

export const CanvasTransformModel = types
  .model("CanvasTransformStore", {
    // Whether canvas transform is enabled (follows layer inspector)
    enabled: types.optional(types.boolean, true),
  })
  .volatile(() => ({
    // Active manipulation state
    activeGesture: "idle" as GestureType,
    activeHandle: undefined as HandlePosition | undefined,

    // Element bounds in screen coordinate space
    elementBounds: null as ElementBounds | null,

    // Transform values being manipulated
    currentTransform: {} as TransformState,
    initialTransform: null as TransformState | null,

    // Initial element bounds when gesture started
    initialBounds: null as ElementBounds | null,

    // Drag start position (screen coordinates)
    dragStartPoint: null as Point | null,

    // Current drag position
    currentPoint: null as Point | null,

    // Video scale factor
    videoScale: 1,

    // Frozen videoScale captured at gesture start
    gestureVideoScale: 1,

    // Video container rect
    containerRect: null as DOMRect | null,

    // Current selection being manipulated
    currentSelection: null as LayerInspectorSelection | null,

    // Selection identity for reset
    currentSelectionKey: "",

    // Scale mode for active gesture
    activeScaleIsAbsolute: false,
    activeScaleXIsAbsolute: false,
    activeScaleYIsAbsolute: false,

    // Provenance flags: whether the layer had explicit mods at gesture start
    activeHasScaleMod: false,
    activeHasScaleXMod: false,
    activeHasScaleYMod: false,

    // rAF id for throttled updates
    pendingRafId: null as number | null,

    // setTimeout id for layer inspector re-enable
    pendingTimeoutId: null as ReturnType<typeof setTimeout> | null,
  }))
  .views((self) => ({
    get isActive(): boolean {
      return self.activeGesture !== "idle";
    },
    get hasSelection(): boolean {
      return self.elementBounds !== null;
    },
    get gestureTransformCSS(): string {
      if (
        self.activeGesture === "idle" ||
        !self.dragStartPoint ||
        !self.currentPoint
      ) {
        return "";
      }

      switch (self.activeGesture) {
        case "drag": {
          const dx = self.currentPoint.x - self.dragStartPoint.x;
          const dy = self.currentPoint.y - self.dragStartPoint.y;
          return `translate(${dx}px, ${dy}px)`;
        }
        case "scale": {
          const scaleFactor = self.currentTransform.scaleFactor ?? 1;
          return `scale(${scaleFactor})`;
        }
        case "rotate": {
          const initialRotation = self.initialTransform?.rotation ?? 0;
          const currentRotation = self.currentTransform.rotation ?? 0;
          const delta = currentRotation - initialRotation;
          return `rotate(${delta}deg)`;
        }
        default:
          return "";
      }
    },
  }))
  .actions((self) => {
    const getLayerForSelection = (
      jsonFile: Animation,
      resolvedLayer: ResolvedLayerSnapshot,
      effectiveAssetId?: string
    ) => {
      const layers = findLayersOrLayerAssets({
        jsonVid: jsonFile,
        layerName: resolvedLayer.layerName,
        assetId: effectiveAssetId,
        layerUid: resolvedLayer.layerUid,
      });
      return Array.isArray(layers) && layers.length ? layers[0] : null;
    };

    const matchesResolvedLayer = (
      mod: any,
      resolvedLayer: ResolvedLayerSnapshot,
      effectiveAssetId?: string
    ): boolean =>
      (mod.moddata.layerUid != null &&
        mod.moddata.layerUid === resolvedLayer.layerUid) ||
      (mod.moddata.layerName === resolvedLayer.layerName &&
        mod.moddata.assetId === effectiveAssetId);

    const getTransformModsForSelection = (
      modsStore: RootInstance["modsStore"],
      resolvedLayer: ResolvedLayerSnapshot,
      effectiveAssetId?: string
    ): Map<transformChangeTypes, TransformModInfo> => {
      const modMap = new Map<transformChangeTypes, TransformModInfo>();
      modsStore.mods.forEach((mod) => {
        if (mod.moddata.type !== modTypes.transform) return;
        const transformType = mod.moddata.transformChangeType;
        if (!transformType) return;
        if (!matchesResolvedLayer(mod, resolvedLayer, effectiveAssetId)) return;
        if (modMap.has(transformType)) return;
        const value =
          typeof mod.moddata.value === "number"
            ? mod.moddata.value
            : Number(mod.moddata.value);
        if (Number.isNaN(value)) return;
        modMap.set(transformType, {
          value,
          isAbsolute: Boolean(mod.moddata.isAbsolute),
        });
      });
      return modMap;
    };

    const getSelectionTransformState = (): SelectionTransformState => {
      const fallback: SelectionTransformState = {
        transform: {
          positionX: 0,
          positionY: 0,
          scale: 100,
          rotation: 0,
        },
        meta: {
          scaleIsAbsolute: false,
          scaleXIsAbsolute: false,
          scaleYIsAbsolute: false,
          hasScaleMod: false,
          hasScaleXMod: false,
          hasScaleYMod: false,
        },
      };

      const root = getRoot<RootInstance>(self);
      const { modsStore, playerStore } = root;
      const selection = self.currentSelection;
      const jsonFile = playerStore.jsonFile;

      if (!selection || !jsonFile) return fallback;

      const resolvedLayer = resolveLayerDetails(
        selection,
        jsonFile,
        mainAssetKey
      );
      if (!resolvedLayer) return fallback;

      const effectiveAssetId =
        resolvedLayer.assetId === mainAssetKey
          ? undefined
          : resolvedLayer.assetId;

      const modMap = getTransformModsForSelection(
        modsStore,
        resolvedLayer,
        effectiveAssetId
      );

      const layer = getLayerForSelection(
        jsonFile,
        resolvedLayer,
        effectiveAssetId
      );
      const layerTransform = layer ? getLayerTransformValues(layer) : {};

      const positionX =
        modMap.get(transformChangeTypes.positionX)?.value ??
        layerTransform.positionX ??
        fallback.transform.positionX;
      const positionY =
        modMap.get(transformChangeTypes.positionY)?.value ??
        layerTransform.positionY ??
        fallback.transform.positionY;
      const rotation =
        modMap.get(transformChangeTypes.rotation)?.value ??
        layerTransform.rotation ??
        fallback.transform.rotation;

      const scaleMod = modMap.get(transformChangeTypes.scale);
      const scaleXMod = modMap.get(transformChangeTypes.scaleX);
      const scaleYMod = modMap.get(transformChangeTypes.scaleY);

      const scale = scaleMod?.value ?? fallback.transform.scale;
      const scaleX =
        scaleXMod?.value ?? scaleMod?.value ?? fallback.transform.scale;
      const scaleY =
        scaleYMod?.value ?? scaleMod?.value ?? fallback.transform.scale;

      return {
        transform: {
          positionX,
          positionY,
          rotation,
          scale,
          scaleX,
          scaleY,
        },
        meta: {
          scaleIsAbsolute: scaleMod?.isAbsolute ?? false,
          scaleXIsAbsolute:
            scaleXMod?.isAbsolute ?? scaleMod?.isAbsolute ?? false,
          scaleYIsAbsolute:
            scaleYMod?.isAbsolute ?? scaleMod?.isAbsolute ?? false,
          hasScaleMod: scaleMod !== undefined,
          hasScaleXMod: scaleXMod !== undefined,
          hasScaleYMod: scaleYMod !== undefined,
        },
      };
    };

    return {
      setEnabled(enabled: boolean) {
        self.enabled = enabled;
      },

      setElementBounds(bounds: ElementBounds | null) {
        self.elementBounds = bounds;
      },

      setVideoScale(scale: number, containerRect: DOMRect | null) {
        self.videoScale = scale;
        self.containerRect = containerRect;
      },

      setCurrentSelection(selection: LayerInspectorSelection | null) {
        const nextKey = buildLayerInspectorSelectionKey(
          selection,
          mainAssetKey
        );
        if (nextKey === self.currentSelectionKey) return;
        self.currentSelectionKey = nextKey;
        this.cancelPendingTransform();
        self.currentSelection = selection;
        self.currentTransform = {};
        self.initialTransform = null;
        self.activeGesture = "idle";
        self.activeHandle = undefined;
        self.dragStartPoint = null;
        self.currentPoint = null;
        self.initialBounds = null;
        self.activeScaleIsAbsolute = false;
        self.activeScaleXIsAbsolute = false;
        self.activeScaleYIsAbsolute = false;
        self.activeHasScaleMod = false;
        self.activeHasScaleXMod = false;
        self.activeHasScaleYMod = false;
        self.gestureVideoScale = 1;
      },

      startGesture(
        type: GestureType,
        startPoint: Point,
        handle?: HandlePosition,
        initialTransform?: TransformState
      ) {
        if (!self.elementBounds) return;

        // Capture undo snapshot before gesture starts
        const root = getRoot<RootInstance>(self);
        root.undoStore.onGestureStart();

        const selectionState = getSelectionTransformState();
        const baseTransform = initialTransform ?? selectionState.transform;

        self.activeGesture = type;
        self.activeHandle = handle;
        self.dragStartPoint = startPoint;
        self.currentPoint = startPoint;
        self.initialBounds = { ...self.elementBounds };
        self.gestureVideoScale = self.videoScale;
        self.initialTransform = baseTransform;
        self.currentTransform = { ...baseTransform };
        self.activeScaleIsAbsolute = selectionState.meta.scaleIsAbsolute;
        self.activeScaleXIsAbsolute = selectionState.meta.scaleXIsAbsolute;
        self.activeScaleYIsAbsolute = selectionState.meta.scaleYIsAbsolute;
        self.activeHasScaleMod = selectionState.meta.hasScaleMod;
        self.activeHasScaleXMod = selectionState.meta.hasScaleXMod;
        self.activeHasScaleYMod = selectionState.meta.hasScaleYMod;
      },

      updateGesture(currentPoint: Point) {
        if (self.activeGesture === "idle" || !self.dragStartPoint) return;

        self.currentPoint = currentPoint;

        const dx = currentPoint.x - self.dragStartPoint.x;
        const dy = currentPoint.y - self.dragStartPoint.y;

        // Convert screen delta to video coordinates
        const videoDx = dx / self.gestureVideoScale;
        const videoDy = dy / self.gestureVideoScale;

        switch (self.activeGesture) {
          case "drag":
            self.currentTransform = {
              ...self.currentTransform,
              positionX: (self.initialTransform?.positionX || 0) + videoDx,
              positionY: (self.initialTransform?.positionY || 0) + videoDy,
            };
            break;

          case "scale":
            if (self.initialBounds) {
              const center = {
                x: self.initialBounds.centerX,
                y: self.initialBounds.centerY,
              };
              const rotationRad = (self.initialBounds.rotation * Math.PI) / 180;
              const cos = Math.cos(-rotationRad);
              const sin = Math.sin(-rotationRad);

              const toLocal = (point: Point) => {
                const dxLocal = point.x - center.x;
                const dyLocal = point.y - center.y;
                return {
                  x: dxLocal * cos - dyLocal * sin,
                  y: dxLocal * sin + dyLocal * cos,
                };
              };

              const startLocal = toLocal(self.dragStartPoint);
              const currentLocal = toLocal(currentPoint);

              const startDist = Math.hypot(startLocal.x, startLocal.y);
              const currentDist = Math.hypot(currentLocal.x, currentLocal.y);
              const scaleFactor = Math.max(
                0.01,
                startDist > 0 ? currentDist / startDist : 1
              );

              self.currentTransform = {
                ...self.currentTransform,
                scale: undefined,
                scaleX: undefined,
                scaleY: undefined,
                scaleFactor,
              };
            }
            break;

          case "rotate":
            if (self.initialBounds) {
              const center = {
                x: self.initialBounds.centerX,
                y: self.initialBounds.centerY,
              };

              // Calculate angles
              const startAngle = Math.atan2(
                self.dragStartPoint.y - center.y,
                self.dragStartPoint.x - center.x
              );
              const currentAngle = Math.atan2(
                currentPoint.y - center.y,
                currentPoint.x - center.x
              );

              // Calculate rotation delta in degrees
              const rotationDelta =
                ((currentAngle - startAngle) * 180) / Math.PI;
              const baseRotation = self.initialTransform?.rotation || 0;

              self.currentTransform = {
                ...self.currentTransform,
                rotation: baseRotation + rotationDelta,
              };
            }
            break;
        }

        // Apply live transform to ModsStore
        this.scheduleLiveTransform();
      },

      nudgeSelection(deltaX: number, deltaY: number): boolean {
        const root = getRoot<RootInstance>(self);
        const { playerStore } = root;
        const jsonFile = playerStore.jsonFile;
        const selection =
          self.currentSelection ?? playerStore.layerInspectorPrimarySelection;

        if (!selection || !jsonFile) {
          return false;
        }

        const resolvedLayer = resolveLayerDetails(
          selection,
          jsonFile,
          mainAssetKey
        );
        if (!resolvedLayer) {
          return false;
        }

        const effectiveAssetId =
          resolvedLayer.assetId === mainAssetKey
            ? undefined
            : resolvedLayer.assetId;
        const directRootLayerPath =
          typeof selection.path === "string" &&
          /^layers\[\d+\]$/.test(selection.path)
            ? selection.path
            : undefined;
        const target: MotionTargetQuery = {
          layerName: resolvedLayer.layerName,
          layerUid: resolvedLayer.layerUid,
          ...(effectiveAssetId ? { assetId: effectiveAssetId } : {}),
          ...(directRootLayerPath ? { path: directRootLayerPath } : {}),
        };
        const capabilities = (() => {
          try {
            return motionAPI.inspect.getEditableLayerCapabilities(
              jsonFile as any,
              target,
              { frame: playerStore.currentFrame }
            )[0];
          } catch {
            return undefined;
          }
        })();
        const positionField = capabilities?.fields.find(
          (field) => field.property === "position"
        );
        const currentPosition = toPositionVector(positionField?.value);
        if (!positionField || !currentPosition || !capabilities?.target?.path) {
          return false;
        }

        const nextPosition = [
          currentPosition[0] + deltaX,
          currentPosition[1] + deltaY,
          currentPosition[2] ?? 0,
        ];
        const layerTransform = getSelectionTransformState().transform;

        self.currentTransform = {
          ...layerTransform,
          positionX: nextPosition[0],
          positionY: nextPosition[1],
        };

        return playerStore.handleLayerInspectorTransformCommit({
          target: {
            path: capabilities.target.path,
            layerName: capabilities.target.layerName,
            layerUid: capabilities.target.layerUid,
            assetName: capabilities.target.assetId ?? selection.assetName,
          },
          frame: playerStore.currentFrame,
          interaction: "move",
          changes: [
            {
              property: "position",
              path: positionField.path,
              previousValue: currentPosition,
              value: nextPosition,
            },
          ],
        });
      },

      applyLiveTransform() {
        if (self.pendingRafId !== null) {
          self.pendingRafId = null;
        }
        const root = getRoot<RootInstance>(self);
        const { modsStore } = root;
        const selection = self.currentSelection;

        if (!selection || !self.currentTransform) return;

        const gestureType = self.activeGesture;
        const transform = self.currentTransform;

        // Find or update transform mods based on gesture type
        if (gestureType === "drag") {
          if (transform.positionX !== undefined) {
            this.updateOrCreateTransformMod(
              modsStore,
              selection,
              transformChangeTypes.positionX,
              transform.positionX,
              true
            );
          }
          if (transform.positionY !== undefined) {
            this.updateOrCreateTransformMod(
              modsStore,
              selection,
              transformChangeTypes.positionY,
              transform.positionY,
              true
            );
          }
        } else if (gestureType === "scale") {
          if (transform.scaleFactor !== undefined) {
            const factor = transform.scaleFactor;
            const hasSplitStart =
              self.activeHasScaleXMod || self.activeHasScaleYMod;

            if (hasSplitStart) {
              const baseScaleX =
                self.initialTransform?.scaleX ??
                self.initialTransform?.scale ??
                100;
              const baseScaleY =
                self.initialTransform?.scaleY ??
                self.initialTransform?.scale ??
                100;
              this.updateOrCreateTransformMod(
                modsStore,
                selection,
                transformChangeTypes.scaleX,
                baseScaleX * factor,
                self.activeScaleXIsAbsolute
              );
              this.updateOrCreateTransformMod(
                modsStore,
                selection,
                transformChangeTypes.scaleY,
                baseScaleY * factor,
                self.activeScaleYIsAbsolute
              );
              // Canonicalize: remove conflicting uniform scale mod
              this.removeConflictingScaleMod(
                modsStore,
                selection,
                transformChangeTypes.scale
              );
            } else {
              const baseScale = self.initialTransform?.scale ?? 100;
              this.updateOrCreateTransformMod(
                modsStore,
                selection,
                transformChangeTypes.scale,
                baseScale * factor,
                self.activeScaleIsAbsolute
              );
              // Canonicalize: remove conflicting split-scale mods
              this.removeConflictingScaleMod(
                modsStore,
                selection,
                transformChangeTypes.scaleX
              );
              this.removeConflictingScaleMod(
                modsStore,
                selection,
                transformChangeTypes.scaleY
              );
            }
          }
        } else if (
          gestureType === "rotate" &&
          transform.rotation !== undefined
        ) {
          this.updateOrCreateTransformMod(
            modsStore,
            selection,
            transformChangeTypes.rotation,
            transform.rotation,
            true
          );
        }
      },

      updateOrCreateTransformMod(
        modsStore: RootInstance["modsStore"],
        selection: LayerInspectorSelection,
        transformType: transformChangeTypes,
        value: number,
        isAbsolute: boolean
      ) {
        const root = getRoot<RootInstance>(self);
        const { playerStore } = root;
        const jsonFile = playerStore.jsonFile;

        if (!jsonFile) return;

        // Resolve the actual layer details from the selection
        // This handles the case where layerName might be "uid_XXXX" instead of actual name
        const resolvedLayer = resolveLayerDetails(
          selection,
          jsonFile,
          mainAssetKey
        );
        if (!resolvedLayer) {
          console.warn(
            "[CanvasTransform] Could not resolve layer details",
            selection
          );
          return;
        }

        const { layerName, layerUid, assetId } = resolvedLayer;
        // Don't use assetId if it's the main asset (same as fallback)
        const effectiveAssetId = assetId === mainAssetKey ? undefined : assetId;

        // Find existing mod for this layer and transform type
        const existingMod = modsStore.mods.find(
          (mod) =>
            mod.moddata.type === modTypes.transform &&
            mod.moddata.transformChangeType === transformType &&
            ((layerUid != null && mod.moddata.layerUid === layerUid) ||
              (mod.moddata.layerName === layerName &&
                mod.moddata.assetId === effectiveAssetId))
        );

        if (existingMod) {
          // Update existing mod
          if (existingMod.moddata.isAbsolute !== isAbsolute) {
            modsStore.updateValue(existingMod.id, "isAbsolute", isAbsolute);
          }
          modsStore.updateValue(
            existingMod.id,
            "value",
            Math.round(value * 100) / 100
          );
        } else {
          // Create new mod
          // We already have the layer info resolved, so we pass it directly
          // Don't pass prefillSelections so the new connector can use the
          // current inspector selection after the mod is created.
          modsStore.addMod(
            {
              type: modTypes.transform,
              transformChangeType: transformType,
              layerUid,
              layerName,
              assetId: effectiveAssetId,
              value: Math.round(value * 100) / 100,
              isAbsolute,
            },
            undefined,
            undefined,
            { activate: false, prefill: false }
          );

          // Restore the inspector selection after mod creation
          // because addMod can briefly rebuild the player.
          // Use setTimeout to ensure this happens after the current execution context
          if (selection) {
            if (self.pendingTimeoutId !== null) {
              clearTimeout(self.pendingTimeoutId);
            }
            self.pendingTimeoutId = setTimeout(() => {
              this.restoreLayerInspectorAfterModCreation(selection);
            }, 0);
          }
        }
      },

      restoreLayerInspectorAfterModCreation(
        selection: LayerInspectorSelection
      ) {
        setLayerInspectorEnabled(true);
        setLayerInspectorSelections([selection]);
        self.pendingTimeoutId = null;
      },

      removeConflictingScaleMod(
        modsStore: RootInstance["modsStore"],
        selection: LayerInspectorSelection,
        transformType: transformChangeTypes
      ) {
        const root = getRoot<RootInstance>(self);
        const { playerStore } = root;
        const jsonFile = playerStore.jsonFile;
        if (!jsonFile) return;

        const resolvedLayer = resolveLayerDetails(
          selection,
          jsonFile,
          mainAssetKey
        );
        if (!resolvedLayer) return;

        const { layerName, layerUid, assetId } = resolvedLayer;
        const effectiveAssetId = assetId === mainAssetKey ? undefined : assetId;

        const conflictingMod = modsStore.mods.find(
          (mod) =>
            mod.moddata.type === modTypes.transform &&
            mod.moddata.transformChangeType === transformType &&
            ((layerUid != null && mod.moddata.layerUid === layerUid) ||
              (mod.moddata.layerName === layerName &&
                mod.moddata.assetId === effectiveAssetId))
        );

        if (conflictingMod) {
          modsStore.removeMod(conflictingMod);
        }
      },

      scheduleLiveTransform() {
        if (self.pendingRafId !== null) return;
        self.pendingRafId = window.requestAnimationFrame(() => {
          this.applyLiveTransform();
        });
      },

      flushLiveTransform() {
        if (self.pendingRafId !== null) {
          window.cancelAnimationFrame(self.pendingRafId);
          self.pendingRafId = null;
        }
        this.applyLiveTransform();
      },

      cancelPendingTransform() {
        if (self.pendingRafId !== null) {
          window.cancelAnimationFrame(self.pendingRafId);
          self.pendingRafId = null;
        }
      },

      endGesture() {
        this.flushLiveTransform();

        // Finalize undo snapshot after gesture ends
        const root = getRoot<RootInstance>(self);
        root.undoStore.onGestureEnd();

        // Reset gesture state
        self.activeGesture = "idle";
        self.activeHandle = undefined;
        self.dragStartPoint = null;
        self.currentPoint = null;
        self.initialBounds = null;
        self.initialTransform = null;
        self.activeScaleIsAbsolute = false;
        self.activeScaleXIsAbsolute = false;
        self.activeScaleYIsAbsolute = false;
        self.activeHasScaleMod = false;
        self.activeHasScaleXMod = false;
        self.activeHasScaleYMod = false;
        self.gestureVideoScale = 1;
      },

      reset() {
        this.cancelPendingTransform();
        if (self.pendingTimeoutId !== null) {
          clearTimeout(self.pendingTimeoutId);
          self.pendingTimeoutId = null;
        }
        self.activeGesture = "idle";
        self.activeHandle = undefined;
        self.elementBounds = null;
        self.currentTransform = {};
        self.initialTransform = null;
        self.initialBounds = null;
        self.dragStartPoint = null;
        self.currentPoint = null;
        self.currentSelection = null;
        self.currentSelectionKey = "";
        self.activeScaleIsAbsolute = false;
        self.activeScaleXIsAbsolute = false;
        self.activeScaleYIsAbsolute = false;
        self.activeHasScaleMod = false;
        self.activeHasScaleXMod = false;
        self.activeHasScaleYMod = false;
        self.gestureVideoScale = 1;
      },

      beforeDestroy() {
        if (self.pendingRafId !== null) {
          window.cancelAnimationFrame(self.pendingRafId);
        }
        if (self.pendingTimeoutId !== null) {
          clearTimeout(self.pendingTimeoutId);
        }
      },
    };
  });

export type ICanvasTransformModel = Instance<typeof CanvasTransformModel>;
